import React, { useState, useRef, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    TextField, // Added for potential display, though not strictly needed for this version
} from "@mui/material";
import {
    PhotoCamera as CameraIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material"; // MUI Icon
import { useAppDispatch } from "../../../store/hook"; // Adjusted path for useAppDispatch
import {
    fetchFullProfile,
    updateProfile,
    Experience,
    Education,
    VolunteerExperience,
    LicenseCertification,
} from "../../../store/slices/profileSlice"; // To refresh profile and update
import type { ProfileState, Location as ProfileLocation } from "../../../store/slices/profileSlice"; // Import ProfileState and Location
import { RootState } from "../../../store/store"; // Import RootState
import { useSelector } from "react-redux"; // Import useSelector
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.min.mjs";

// Types for the profile data that will be extracted by AI
// Aligned with fields from profileSlice.ts Profile interface that are realistically extractable
interface ProfileData {
    firstName?: string;
    lastName?: string;
    title?: string; // e.g., "Software Engineer at Google"
    location?: {
        // Attempt to get a structured location
        city?: string;
        state?: string; // Province/State
        country?: string;
    };
    about?: string; // User's bio or summary
    profilePictureUrl?: string; // URL of the user's profile picture
    experience?: AIExtractedExperience[]; // Array of experiences
    volunteerExperience?: AIExtractedVolunteerExperience[]; // Array of volunteer experiences
    education?: AIExtractedEducation[]; // Array of education experiences
    licenseCertification?: AIExtractedLicenseCertification[]; // Array of license certifications
}

// Simplified structure for AI-extracted experience
interface AIExtractedExperience {
    jobTitle?: string;
    companyName?: string;
    startDate?: string; // e.g., "2020-01-01"
    endDate?: string; // e.g., "2022-12-31"
    currentlyWorkHere?: boolean;
    description?: string;
}

// Simplified structure for AI-extracted volunteer experience
interface AIExtractedVolunteerExperience {
    role?: string;
    organization?: string;
    startDate?: string; // e.g., "2022-06-01"
    endDate?: string; // e.g., "2023-12-01"
    currentlyVolunteerHere?: boolean;
    description?: string;
}

// Simplified structure for AI-extracted education
interface AIExtractedEducation {
    school?: string;
    degree?: string;
    fieldOfStudy?: string;
    from?: string; // e.g., "2014-09-01"
    to?: string; // e.g., "2018-05-01"
    current?: boolean;
    description?: string;
}

// Simplified structure for AI-extracted license/certification
interface AIExtractedLicenseCertification {
    name?: string;
    issuingOrganization?: string;
    issueDate?: string; // e.g., "2021-03-01"
    expirationDate?: string; // e.g., "2023-03-01"
    neverExpires?: boolean;
    credentialID?: string;
}

// Configuration for the AI service
interface AIServiceConfig {
    apiEndpoint: string;
    apiKey: string;
    model: string;
    promptTemplate: string;
}

// Configure to use ChatGPT as requested by user
const chatGptConfig: AIServiceConfig = {
    apiEndpoint: "https://api.openai.com/v1/chat/completions",
    //   apiKey: process.env.REACT_APP_OPENAI_API_KEY || '' , // Ensure this is set in your .env file
    apiKey: import.meta.env.VITE_APP_OPENAI_API_KEY || "", // Correct model for vision capabilities
    model: "gpt-4o", // Correct model for vision capabilities
    promptTemplate: `
    Analyze the provided profile screenshot(s) or resume pages from a professional social media platform or document.
    Extract the following information into a structured JSON object.
    The JSON should only contain the keys specified below.

    - "firstName": The user's first name.
    - "lastName": The user's last name.
    - "title": The user's current professional title or headline (e.g., "Software Engineer at Tech Corp", "Marketing Manager").
    - "location": An object containing "city", "state" (province or state), and "country". If specific parts are not available, omit them or use null.
    - "about": The user's summary or bio section.
    - "profilePictureUrl": The direct URL to the user's profile picture, if visible and extractable.
    - "experience": An array of objects, where each object represents a distinct work experience. Each object should have:
        - "jobTitle": (string) The role or title.
        - "companyName": (string) The name of the company.
        - "startDate": (string) The start date in "YYYY-MM-DD" format.
        - "endDate": (string, optional) The end date in "YYYY-MM-DD" format. Omit or use null if it's the current position.
        - "currentlyWorkHere": (boolean) True if this is the current position, otherwise false.
        - "description": (string, optional) A brief description of the role or achievements, if available.
      If no experience section is found, return an empty array: [].
    - "volunteerExperience": An array of objects, where each object represents a distinct volunteer experience. Each object should have:
        - "role": (string) The volunteer role.
        - "organization": (string) The name of the organization.
        - "startDate": (string) The start date in "YYYY-MM-DD" format.
        - "endDate": (string, optional) The end date in "YYYY-MM-DD" format. Omit or use null if it's the current role.
        - "currentlyVolunteerHere": (boolean) True if this is the current role, otherwise false.
        - "description": (string, optional) A brief description of the volunteer work.
      If no volunteer experience section is found, return an empty array: [].
    - "education": An array of objects, where each object represents a distinct education entry. Each object should have:
        - "school": (string) The name of the institution.
        - "degree": (string, optional) The degree obtained (e.g., "Bachelor of Science").
        - "fieldOfStudy": (string, optional) The field of study (e.g., "Computer Science").
        - "from": (string) The start date in "YYYY-MM-DD" format.
        - "to": (string, optional) The end date in "YYYY-MM-DD" format. Omit or use null if currently studying.
        - "current": (boolean) True if currently studying here, otherwise false.
        - "description": (string, optional) A brief description or achievements.
      If no education section is found, return an empty array: [].
    - "licenseCertification": An array of objects, where each object represents a license or certification. Each object should have:
        - "name": (string) The name of the license or certification.
        - "issuingOrganization": (string) The organization that issued it.
        - "issueDate": (string) The issue date in "YYYY-MM-DD" format.
        - "expirationDate": (string, optional) The expiration date in "YYYY-MM-DD" format. Omit or use null if it doesn't expire.
        - "neverExpires": (boolean) True if the certification does not expire.
        - "credentialID": (string, optional) The credential ID, if available.
      If no license/certification section is found, return an empty array: [].


    Respond ONLY with the valid JSON object. Do not include any additional text, markdown formatting (like \\\\\`\\\\\`\\\\\`json), or explanations.

    Example of the EXACT JSON format required:
    {
      "firstName": "Jane",
      "lastName": "Doe",
      "title": "Senior Product Manager at Innovate Solutions",
      "location": {
        "city": "Toronto",
        "state": "ON",
        "country": "Canada"
      },
      "about": "Passionate about building impactful products and leading high-performing teams. 10+ years of experience in product strategy and development.",
      "profilePictureUrl": "https://example.com/path/to/jane_doe_profile.jpg",
      "experience": [
        {
          "jobTitle": "Senior Product Manager",
          "companyName": "Innovate Solutions",
          "startDate": "2020-01-15",
          "endDate": null,
          "currentlyWorkHere": true,
          "description": "Led product strategy for flagship SaaS offering, resulting in 30% YoY growth."
        },
        {
          "jobTitle": "Product Manager",
          "companyName": "NextGen Apps",
          "startDate": "2017-06-01",
          "endDate": "2019-12-31",
          "currentlyWorkHere": false,
          "description": "Managed the full product lifecycle for two mobile applications."
        }
      ],
      "volunteerExperience": [
        {
          "role": "Mentor",
          "organization": "Tech For All",
          "startDate": "2021-03-01",
          "endDate": null,
          "currentlyVolunteerHere": true,
          "description": "Mentoring aspiring product managers."
        }
      ],
      "education": [
        {
          "school": "University of Example",
          "degree": "Master of Science",
          "fieldOfStudy": "Data Science",
          "from": "2018-09-01",
          "to": "2020-05-20",
          "current": false,
          "description": "Thesis on machine learning applications."
        }
      ],
      "licenseCertification": [
        {
          "name": "Certified Cloud Practitioner",
          "issuingOrganization": "Cloud Services Inc.",
          "issueDate": "2021-08-01",
          "expirationDate": "2024-08-01",
          "neverExpires": false,
          "credentialID": "CERT12345"
        }
      ]
    }

    If a field (like firstName, lastName, title, about, profilePictureUrl) is not found or not applicable, you can omit it from the JSON or set its value to null, but prefer omission for cleaner data.
    For the location, if you can only extract a general string like "San Francisco Bay Area", please try to map it to city, state, and country if possible, otherwise provide what you have in the most structured way possible under the "location" object.
    For experience, volunteerExperience, education, and licenseCertification, if not found, return empty arrays as shown in the instructions.
  `,
};

// The URL for your backend API
// This will be replaced by dispatching updateProfile
// const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || '/api/profile/import'; // Ensure this endpoint exists on your backend

interface ImportProfileServiceProps {
    isOpen: boolean;
    onClose: () => void;
    token: string | null; // Auth token needed to refresh profile
}

// Set up the worker for pdfjs-dist
// This is crucial for it to work in a webpack/react environment
GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();

// Helper function to find the end of a JSON value (object, array, string, or primitive)
const findValueEnd = (str: string, startIndex: number): number => {
    let i = startIndex;
    // Skip leading whitespace
    while (i < str.length && /\s/.test(str[i])) {
        i++;
    }
    const startChar = str[i];

    // Case 1: String value
    if (startChar === '"') {
        let currentIndex = i + 1;
        while (currentIndex < str.length) {
            // Find the next unescaped quote
            if (str[currentIndex] === '"' && str[currentIndex - 1] !== "\\") {
                return currentIndex;
            }
            currentIndex++;
        }
        return -1; // Unmatched quote
    }

    // Case 2: Object or Array value
    if (startChar === "{" || startChar === "[") {
        const openChar = startChar;
        const closeChar = openChar === "{" ? "}" : "]";
        let depth = 1;
        let inString = false;
        let currentIndex = i + 1;

        while (currentIndex < str.length) {
            const char = str[currentIndex];
            // Toggle inString state if we see an unescaped quote
            if (char === '"' && str[currentIndex - 1] !== "\\") {
                inString = !inString;
            } else if (!inString) {
                if (char === openChar) {
                    depth++;
                } else if (char === closeChar) {
                    depth--;
                }
            }

            if (depth === 0) {
                return currentIndex;
            }
            currentIndex++;
        }
        return -1; // Unmatched bracket/brace
    }

    // Case 3: Primitive (number, boolean, null)
    let endIndex = i;
    while (endIndex < str.length && !/[,}\]]/.test(str[endIndex])) {
        endIndex++;
    }
    return endIndex - 1;
};

export const ImportProfileService: React.FC<ImportProfileServiceProps> = ({
    isOpen,
    onClose,
    token,
}) => {
    const dispatch = useAppDispatch();
    const currentProfile = useSelector((state: RootState) => state.profile); // Get current profile state
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null); // Changed to string for success message
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Use ChatGPT configuration
    const aiConfig = chatGptConfig;

    // Reset state when modal is closed or operation is finished
    const resetModalState = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setError(null);
        setSuccess(null);
        setIsLoading(false);
        // Also reset the file input value if the ref is available
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Use an effect to reset state when the modal is closed externally
    useEffect(() => {
        if (!isOpen) {
            resetModalState();
        }
    }, [isOpen]);

    const processFile = (file: File | null | undefined) => {
        if (!file) {
            setError("No file selected.");
            return;
        }

        if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
            setError("Please select an image file (PNG, JPG, GIF) or a PDF file.");
            setSelectedImage(null);
            setImagePreview(null);
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            // 10MB max size
            setError("File is too large. Please select a file under 10MB.");
            setSelectedImage(null);
            setImagePreview(null);
            return;
        }

        setSelectedImage(file);
        setError(null); // Clear previous errors
        setSuccess(null); // Clear previous success messages

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        processFile(file);
    };

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    };

    const convertImageToBase64 = (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve([reader.result as string]); // Return as array for consistency
            reader.onerror = (errorEvent) => reject(errorEvent);
            reader.readAsDataURL(file);
        });
    };

    const convertPdfToImagesBase64 = async (file: File): Promise<string[]> => {
        const images: string[] = [];
        const fileReader = new FileReader();

        return new Promise((resolve, reject) => {
            fileReader.onload = async (e) => {
                try {
                    const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);
                    const pdf: PDFDocumentProxy = await getDocument(typedArray).promise;

                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page: PDFPageProxy = await pdf.getPage(i);
                        const viewport = page.getViewport({ scale: 1.5 });
                        const canvas = document.createElement("canvas");
                        const context = canvas.getContext("2d");

                        if (!context) {
                            throw new Error("Could not get canvas context");
                        }

                        canvas.height = viewport.height;
                        canvas.width = viewport.width;

                        await page.render({ canvasContext: context, viewport: viewport }).promise;
                        images.push(canvas.toDataURL("image/jpeg"));
                    }
                    resolve(images);
                } catch (error) {
                    reject(error);
                }
            };
            fileReader.readAsArrayBuffer(file);
        });
    };

    const extractProfileDataWithAI = async (base64Images: string[]): Promise<ProfileData> => {
        // OpenAI expects the image data *without* the 'data:image/...;base64,' prefix for the gpt-4-vision-preview model when sending base64 directly in the image_url.url field.
        // However, the prompt uses image_url, so it expects a URL. If sending raw base64, it should be like:
        // { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${base64Data}` } }
        // Let's ensure the prefix is correctly handled based on how it's used.
        // The current chatGptConfig prompt implies sending data URI.

        const mediaType = selectedImage?.type || "image/jpeg"; // Get media type from selected file

        const imageContent = base64Images.map((base64Image) => ({
            type: "image_url",
            image_url: {
                url: base64Image,
            },
        }));

        try {
            console.log("Sending to AI:", aiConfig.model);
            console.log("AI Config", aiConfig);
            const response = await fetch(aiConfig.apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${aiConfig.apiKey}`,
                },
                body: JSON.stringify({
                    model: aiConfig.model,
                    messages: [
                        {
                            role: "user",
                            content: [
                                { type: "text", text: aiConfig.promptTemplate },
                                ...imageContent,
                            ],
                        },
                    ],
                    max_tokens: 2000, // MAX tokens for the response
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({})); // Try to parse error details
                console.error("AI API error response:", errorData);
                throw new Error(
                    `AI API error: ${response.status} ${response.statusText}. ${errorData.error?.message || ""}`,
                );
            }

            const data = await response.json();
            const content = data.choices?.[0]?.message?.content || "";
            // console.log("AI Response Content:", content);

            // Attempt to parse JSON from the content. AI might sometimes include ```json ... ```
            let jsonData = content;
            if (jsonData.startsWith("```json")) {
                jsonData = jsonData.substring(7, jsonData.length - 3).trim();
            } else if (jsonData.startsWith("```")) {
                jsonData = jsonData.substring(3, jsonData.length - 3).trim();
            }

            try {
                // Attempt to parse the entire JSON string first. This is the happy path.
                return JSON.parse(jsonData);
            } catch (e) {
                console.error(
                    "Failed to parse the entire JSON from AI. Attempting partial recovery.",
                    e,
                );

                const salvagedData: Partial<ProfileData> = {};
                const keysToExtract: (keyof ProfileData)[] = [
                    "firstName",
                    "lastName",
                    "title",
                    "location",
                    "about",
                    "profilePictureUrl",
                    "experience",
                    "volunteerExperience",
                    "education",
                    "licenseCertification",
                ];

                keysToExtract.forEach((key) => {
                    const searchKey = `"${key}":`;
                    const keyIndex = jsonData.indexOf(searchKey);
                    if (keyIndex === -1) return;

                    const valueStartIndex = keyIndex + searchKey.length;
                    const valueEndIndex = findValueEnd(jsonData, valueStartIndex);

                    if (valueEndIndex !== -1) {
                        const valueString = jsonData.substring(valueStartIndex, valueEndIndex + 1);
                        try {
                            const parsedValue = JSON.parse(valueString);
                            salvagedData[key] = parsedValue;
                            console.log(`Successfully salvaged key: "${key}"`);
                        } catch (parseError) {
                            console.error(
                                `Could not parse salvaged value for key "${key}". Error:`,
                                parseError,
                            );
                            console.error(`Value string for key "${key}" was:`, valueString);
                        }
                    }
                });

                if (Object.keys(salvagedData).length > 0) {
                    console.log(
                        "Successfully salvaged the following keys:",
                        Object.keys(salvagedData),
                    );
                    return salvagedData as ProfileData;
                } else {
                    console.error("Partial recovery failed. Could not salvage any data.");
                    // Re-throw the original error if nothing could be salvaged.
                    throw e;
                }
            }
        } catch (error) {
            console.error("Error extracting profile data with AI:", error);
            // Re-throw to be caught by handleSubmit
            if (error instanceof Error) throw error;
            throw new Error("An unknown error occurred during AI data extraction.");
        }
    };

    const handleSubmit = async () => {
        if (!selectedImage) {
            setError("Please select a file first.");
            return;
        }
        if (!token) {
            setError("Authentication token is missing. Cannot update profile.");
            setIsLoading(false);
            return;
        }
        if (!currentProfile.id) {
            setError("Current profile ID is missing. Cannot update profile.");
            console.error(
                "Current profile ID (currentProfile.id) is missing from Redux state.",
                currentProfile,
            );
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            let base64Images: string[];
            if (selectedImage.type.startsWith("image/")) {
                // 1a. Convert image to base64 (with data URI prefix)
                base64Images = await convertImageToBase64(selectedImage);
            } else if (selectedImage.type === "application/pdf") {
                // 1b. Convert PDF to base64 images
                base64Images = await convertPdfToImagesBase64(selectedImage);
                // For PDFs, we don't have a single simple preview, so we'll just show a success message.
                // We could enhance this to show thumbnails of each page if needed.
                setImagePreview(null); // Clear image preview if it was a PDF
            } else {
                throw new Error("Unsupported file type.");
            }

            // 2. Extract profile data using AI
            const extractedProfileData = await extractProfileDataWithAI(base64Images);
            // console.log("Extracted Profile Data from AI:", extractedProfileData);

            // Log extracted complex array fields if present and non-empty
            if (extractedProfileData.experience && extractedProfileData.experience.length > 0) {
                // console.log("AI Extracted Experience:", extractedProfileData.experience);
            }
            if (
                extractedProfileData.volunteerExperience &&
                extractedProfileData.volunteerExperience.length > 0
            ) {
                console.log(
                    "AI Extracted Volunteer Experience:",
                    extractedProfileData.volunteerExperience,
                );
            }
            if (extractedProfileData.education && extractedProfileData.education.length > 0) {
                console.log("AI Extracted Education:", extractedProfileData.education);
            }
            if (
                extractedProfileData.licenseCertification &&
                extractedProfileData.licenseCertification.length > 0
            ) {
                console.log(
                    "AI Extracted Licenses & Certifications:",
                    extractedProfileData.licenseCertification,
                );
            }

            // 3. Prepare data and submit using updateProfile thunk
            // Map AI extracted data to the structure of ProfileState, providing defaults for missing fields
            const experienceData = extractedProfileData.experience?.map(
                (exp): Experience => ({
                    jobTitle: exp.jobTitle || "",
                    companyName: exp.companyName || "",
                    startDate: exp.startDate || "",
                    endDate: exp.endDate || "",
                    currentlyWorkHere: exp.currentlyWorkHere || false,
                    description: exp.description || "",
                    employmentType: "", // AI does not extract this, set to default
                    country: "", // AI does not extract this, set to default
                    locationType: "", // AI does not extract this, set to default
                }),
            );

            const educationData = extractedProfileData.education?.map(
                (edu): Education => ({
                    school: edu.school || "",
                    degree: edu.degree || "",
                    fieldOfStudy: edu.fieldOfStudy || "",
                    from: edu.from || "",
                    to: edu.to || "",
                    current: edu.current || false,
                    description: edu.description || "",
                    grade: "", // AI does not extract this, set to default
                }),
            );

            const volunteerData = extractedProfileData.volunteerExperience?.map(
                (vol): VolunteerExperience => ({
                    role: vol.role || "",
                    organization: vol.organization || "",
                    startDate: vol.startDate || "",
                    endDate: vol.endDate || "",
                    currentlyVolunteerHere: vol.currentlyVolunteerHere || false,
                    description: vol.description || "",
                    cause: "", // AI does not extract this, set to default
                }),
            );

            const licenseData = extractedProfileData.licenseCertification?.map(
                (lic): LicenseCertification => ({
                    name: lic.name || "",
                    issuingOrganization: lic.issuingOrganization || "",
                    issueDate: lic.issueDate || "",
                    expirationDate: lic.expirationDate || "",
                    neverExpires: lic.neverExpires || false,
                    credentialID: lic.credentialID || "",
                    credentialURL: "", // AI does not extract this, set to default
                }),
            );

            const profileDataForUpdate: ProfileState = {
                ...currentProfile, // Start with the full current profile from Redux
                id: currentProfile.id, // Ensure the profile ID is correctly passed
                userId: currentProfile.userId, // Ensure userId is correctly passed

                // Overwrite with extracted data where available
                firstName: extractedProfileData.firstName || currentProfile.firstName,
                lastName: extractedProfileData.lastName || currentProfile.lastName,
                title: extractedProfileData.title || currentProfile.title,
                about: extractedProfileData.about || currentProfile.about,
                photoUrl: extractedProfileData.profilePictureUrl || currentProfile.photoUrl,
                // Location needs careful handling as AI provides city/state/country
                // and ProfileState expects { country: string, state: string }
                location: extractedProfileData.location
                    ? {
                          country:
                              extractedProfileData.location.country ||
                              currentProfile.location?.country ||
                              "",
                          state:
                              extractedProfileData.location.state ||
                              currentProfile.location?.state ||
                              "",
                          city:
                              extractedProfileData.location.city ||
                              currentProfile.location?.city ||
                              "",
                      }
                    : currentProfile.location,
                // Overwrite array fields ONLY if the AI provided new data
                experience: experienceData || currentProfile.experience,
                education: educationData || currentProfile.education,
                volunteerExperience: volunteerData || currentProfile.volunteerExperience,
                licenseCertification: licenseData || currentProfile.licenseCertification,
                backgroundPhotoUrl: currentProfile.backgroundPhotoUrl,
                initialSetupComplete: currentProfile.initialSetupComplete,
                professionalStatus: currentProfile.professionalStatus,
                createdAt: currentProfile.createdAt,
                updatedAt: currentProfile.updatedAt, // This will be updated by the backend
                // Ensure loading, error, connectionRequestStatus, etc., are from the original currentProfile
                // as they are not part of the data to be updated but part of the ProfileState structure.
                loading: currentProfile.loading,
                error: currentProfile.error,
                connectionRequestStatus: currentProfile.connectionRequestStatus,
                connectionRequestError: currentProfile.connectionRequestError,
                profiles: currentProfile.profiles,
            };

            // console.log("Data being sent to updateProfile:", profileDataForUpdate);

            // Dispatch updateProfile thunk
            await dispatch(updateProfile({ token, profileData: profileDataForUpdate })).unwrap();

            // 4. Show success message
            setSuccess("Profile data extracted and update request sent successfully!");

            // 5. Refresh the full profile from the backend to update Redux state
            // This is good to ensure the local state reflects the backend state post-update.
            await dispatch(fetchFullProfile(token)).unwrap(); // unwrap to catch potential errors from thunk
            setSuccess("Profile imported and updated successfully!"); // Update success message

            // 6. Close modal immediately on success
            onClose(); // Close the main dialog
        } catch (err) {
            console.error("Full import process error:", err);
            setError("Error in importing profile. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDialogClose = () => {
        if (!isLoading) {
            onClose();
            // resetModalState(); // State is reset by useEffect on !isOpen
        }
    };

    return (
        <Dialog open={isOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Import Profile from Screenshot
                <IconButton
                    aria-label="close"
                    onClick={handleDialogClose}
                    disabled={isLoading}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <DialogContentText sx={{ mb: 2 }}>
                    Upload a screenshot or PDF of your profile from another platform (e.g.,
                    LinkedIn). You can click to upload or drag and drop the file into the area
                    below.
                </DialogContentText>

                {!selectedImage ? (
                    <Box
                        onClick={handleUploadButtonClick}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        sx={{
                            p: 4,
                            textAlign: "center",
                            cursor: "pointer",
                            border: (theme) =>
                                `2px dashed ${
                                    isDragging ? theme.palette.primary.main : theme.palette.divider
                                }`,
                            backgroundColor: isDragging ? "action.hover" : "transparent",
                            borderRadius: 2,
                            transition: (theme) =>
                                theme.transitions.create(["background-color", "border-color"]),
                            "&:hover": {
                                borderColor: "primary.main",
                                backgroundColor: "action.hover",
                            },
                            mb: 2,
                        }}
                    >
                        <input
                            type="file"
                            accept="image/*,application/pdf"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                            id="profile-screenshot-upload"
                        />
                        <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                        <Typography variant="h6">Drag & drop or click to upload</Typography>
                        <Typography variant="body2" color="text.secondary">
                            PNG, JPG, GIF or PDF (max 10MB)
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            mb: 2,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={resetModalState}
                            disabled={isLoading}
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                backgroundColor: "rgba(255,255,255,0.7)",
                            }}
                            title="Remove file"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        {selectedImage.type.startsWith("image/") && imagePreview ? (
                            <>
                                <Typography variant="subtitle2">Image Preview:</Typography>
                                <img
                                    src={imagePreview}
                                    alt="Profile Screenshot Preview"
                                    style={{
                                        maxHeight: "200px",
                                        maxWidth: "100%",
                                        borderRadius: "4px",
                                    }}
                                />
                            </>
                        ) : selectedImage.type === "application/pdf" ? (
                            <>
                                <PictureAsPdfIcon sx={{ fontSize: 48, color: "text.secondary" }} />
                                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                    {selectedImage.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    PDF file selected
                                </Typography>
                            </>
                        ) : null}
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}
                {isLoading && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            my: 2,
                        }}
                    >
                        <CircularProgress />
                        <Typography sx={{ mt: 1 }}>Processing file, please wait...</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "16px 24px" }}>
                <Button onClick={handleDialogClose} disabled={isLoading} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!selectedImage || isLoading || !!success} // Disable if successful to prevent re-submission
                    variant="contained"
                >
                    {isLoading ? "Importing..." : "Import Profile"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportProfileService;
